import React, { useState, useEffect, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { Trash2, DollarSign, CheckSquare, ClockIcon, CoinsIcon, TrendingUpIcon, Wallet2, User } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { WorkSessionFormData } from '@/components/WorkSessionForm';
import { useAuthStore } from '@/stores/auth';
import { apiClient } from '@/services/api';
import { WorkSession, DashboardData } from '@/types';
import { errorLog } from '@/utils/logger';
import { captureEvent } from '@/utils/posthog';

type SessionStatus = 'logged' | 'payout_requested' | 'paid' | 'rejected';

interface ExtendedWorkSession extends WorkSession {
  status?: SessionStatus;
  payoutRequestId?: string;
}


const WorkSessionsPage: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<ExtendedWorkSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRequestingPayout, setIsRequestingPayout] = useState(false);
  const [selectedSessionIds, setSelectedSessionIds] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  const isAdmin = user?.role === 'admin';

  // Heperum theme inline form state
  const getCurrentTime = () => {
    const now = new Date();
    return now.toTimeString().slice(0, 5); // "HH:MM"
  };
  const [startTime, setStartTime] = useState(getCurrentTime);
  const [endTime, setEndTime] = useState(getCurrentTime);
  const [description, setDescription] = useState('');
  const [sessionDate, setSessionDate] = useState(() => new Date().toISOString().split('T')[0]);

  // Fetch dashboard stats for Heperum theme
  const { data: dashboardData } = useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const response = await apiClient.users.getDashboard();
      return response.data as DashboardData;
    },
  });

  // Fetch wallet balance for Heperum theme
  const { data: balanceData, isLoading: isLoadingBalance, isError: isBalanceError } = useQuery({
    queryKey: ['wallet-balance'],
    queryFn: async () => {
      const response = await apiClient.users.getBalance();
      return response.data;
    },
    enabled: !!user?.walletAddress,
    staleTime: 30000,
    retry: 1,
  });

  const stats = dashboardData?.stats || {
    totalSessions: 0,
    totalMinutes: 0,
    totalHours: 0,
    totalCoinsEarned: 0,
    sessionsToday: 0,
    minutesToday: 0,
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.users.getWorkSessions();
      if (response.success && response.data) {
        setSessions(response.data);
      }
    } catch (error) {
      errorLog('Failed to fetch work sessions:', error);
      toast.error('Failed to load work sessions');
    } finally {
      setIsLoading(false);
    }
  };

  // Get sessions that can be selected for payout (logged or rejected)
  const eligibleSessions = useMemo(() => {
    return sessions.filter(s => {
      const status = s.status || (s.isApproved ? 'paid' : 'logged');
      return status === 'logged' || status === 'rejected';
    });
  }, [sessions]);

  // Calculate selected hours
  const selectedHours = useMemo(() => {
    return eligibleSessions
      .filter(s => selectedSessionIds.has(s.id))
      .reduce((total, s) => total + s.durationMinutes / 60, 0);
  }, [eligibleSessions, selectedSessionIds]);

  const canRequestPayout = selectedHours >= 8;

  // Pagination for Heperum theme
  const totalPages = Math.ceil(sessions.length / ITEMS_PER_PAGE);
  const paginatedSessions = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return sessions.slice(startIndex, endIndex);
  }, [sessions, currentPage]);

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
  };

  const handleSelectSession = (sessionId: string) => {
    setSelectedSessionIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedSessionIds.size === eligibleSessions.length) {
      setSelectedSessionIds(new Set());
    } else {
      setSelectedSessionIds(new Set(eligibleSessions.map(s => s.id)));
    }
  };

  const handleRequestPayout = async () => {
    if (!user?.walletAddress) {
      toast.error('Add your wallet address in Profile before requesting a payout');
      navigate('/profile');
      return;
    }

    if (!canRequestPayout) {
      toast.error('Minimum 8 hours required for payout request');
      return;
    }

    try {
      setIsRequestingPayout(true);
      const response = await apiClient.payoutRequests.create({
        sessionIds: Array.from(selectedSessionIds)
      });

      if (response.success) {
        captureEvent('payout_request_created', {
          session_count: selectedSessionIds.size,
          total_hours: Number(selectedHours.toFixed(2)),
          amount_hep: Number(selectedHours.toFixed(2)),
        });
        toast.success('Payout request submitted successfully!');
        setSelectedSessionIds(new Set());
        await fetchSessions();
      }
    } catch (error: any) {
      errorLog('Failed to request payout:', error);
      toast.error(error.message || 'Failed to request payout');
    } finally {
      setIsRequestingPayout(false);
    }
  };

  const calculateTotalHours = () => {
    return sessions.reduce((total, session) => {
      return total + (session.durationMinutes / 60);
    }, 0);
  };

  const formatTotalTime = () => {
    const totalHours = calculateTotalHours();
    const hours = Math.floor(totalHours);
    const minutes = Math.round((totalHours - hours) * 60);
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  const getSessionStatus = (session: ExtendedWorkSession): SessionStatus => {
    if (session.status) return session.status;
    // Fallback for old data
    return session.isApproved ? 'paid' : 'logged';
  };

  const getStatusColor = (status: SessionStatus) => {
    switch (status) {
      case 'paid': return 'text-green-600';
      case 'payout_requested': return 'text-blue-600';
      case 'rejected': return 'text-red-600';
      case 'logged': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };


  const getStatusText = (session: ExtendedWorkSession) => {
    const status = getSessionStatus(session);
    switch (status) {
      case 'paid':
        return 'Paid';
      case 'payout_requested':
        return 'Payout Requested';
      case 'rejected':
        return 'Rejected';
      case 'logged':
        return 'Logged';
      default:
        return 'Unknown';
    }
  };

  const isSessionSelectable = (session: ExtendedWorkSession): boolean => {
    const status = getSessionStatus(session);
    return status === 'logged' || status === 'rejected';
  };

  // Handle form submission
  const handleSubmit = async (data: WorkSessionFormData) => {
    if (!user) return;

    try {
      setIsSubmitting(true);
      const response = await apiClient.workSessions.create(data);
      if (response.success) {
        const start = new Date(`2000-01-01T${data.startTime}`);
        const end = new Date(`2000-01-01T${data.endTime}`);
        captureEvent('work_session_logged', {
          hours: Number(((end.getTime() - start.getTime()) / 3600000).toFixed(2)),
        });
        await fetchSessions();
        // Reset inline form
        setStartTime('13:34');
        setEndTime('13:34');
        setDescription('');
        toast.success('Work session logged successfully!');
      }
    } catch (error: any) {
      errorLog('Failed to create work session:', error);
      toast.error(error.message || 'Failed to log work session');
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Heperum inline form submission
  const handleHeperumSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!startTime || !endTime) {
      toast.error('Please fill in both start and end times');
      return;
    }

    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);

    if (end <= start) {
      toast.error('End time must be after start time');
      return;
    }

    const diffMs = end.getTime() - start.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);

    if (diffHours > 8) {
      toast.error('Work sessions cannot exceed 8 hours per day');
      return;
    }

    const data: WorkSessionFormData = {
      date: isAdmin && sessionDate ? sessionDate : new Date().toISOString().split('T')[0],
      startTime,
      endTime,
      description: description || undefined
    };

    await handleSubmit(data);
  };

  const getUserInitial = () => {
    return user?.fullName?.charAt(0).toUpperCase() || 'U';
  };

  const getUserDisplayName = () => {
    return user?.fullName?.split(' ')[0] || 'User';
  };

  const handleDelete = async (sessionId: string, sessionDate: string) => {
    if (!window.confirm(`Are you sure you want to delete the work session from ${format(parseISO(sessionDate), 'MMM d, yyyy')}?`)) {
      return;
    }

    try {
      const response = await apiClient.workSessions.delete(sessionId);
      if (response.success) {
        toast.success('Work session deleted successfully');
        setSelectedSessionIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(sessionId);
          return newSet;
        });
        await fetchSessions();
      }
    } catch (error: any) {
      errorLog('Failed to delete work session:', error);
      toast.error(error.message || 'Failed to delete work session');
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div
          className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto"
          style={{ borderColor: '#F4D03F' }}
        ></div>
      </div>
    );
  }

  return (
      <div className="text-center text-white">
        {/* Header */}
        <div className="grid grid-cols-3 items-center mb-8">
          <div /> {/* left spacer */}
          <h1 className="text-4xl font-bold text-white text-center">Time Log</h1>
          <div className="flex justify-end">
            <button
              onClick={() => navigate('/profile')}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-teal-400 transition-colors flex items-center gap-2"
            >
              <User className="h-4 w-4" />
              <span className="hidden sm:inline">Profile</span>
            </button>
          </div>
        </div>

        {/* User Avatar and Welcome */}
        <div className="mb-8">
          <div className="heperum-avatar mx-auto mb-4">
            {getUserInitial()}
          </div>
          <h2 className="text-xl mb-2">Welcome {getUserDisplayName()}, to Heperum Time Log</h2>
          <p className="text-lg">Total time logged: {formatTotalTime()}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Total Hours */}
          <div className="bg-[#2a2a2a] rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-8 w-8 text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-400">Total Hours</p>
                <p className="text-2xl font-semibold text-white">
                  {stats.totalHours.toFixed(1)}
                </p>
              </div>
            </div>
          </div>

          {/* Coins Earned */}
          <div className="bg-[#2a2a2a] rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CoinsIcon className="h-8 w-8 text-[#F4D03F]" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-400">Coins Earned</p>
                <p className="text-2xl font-semibold text-white">
                  {stats.totalCoinsEarned.toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          {/* Wallet Balance */}
          <div className="bg-[#2a2a2a] rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Wallet2 className={`h-8 w-8 ${user?.walletAddress ? 'text-teal-400' : 'text-gray-400'}`} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-400">Wallet Balance</p>
                {!user?.walletAddress ? (
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-500">Not connected</span>
                    <button
                      onClick={() => navigate('/profile')}
                      className="text-xs text-teal-400 hover:underline mt-1 text-left"
                    >
                      Link wallet →
                    </button>
                  </div>
                ) : isLoadingBalance ? (
                  <div className="animate-pulse bg-gray-700 h-8 w-20 rounded"></div>
                ) : isBalanceError ? (
                  <p className="text-sm text-gray-400">Unavailable</p>
                ) : (
                  <p className="text-2xl font-semibold text-white">
                    {parseFloat(balanceData?.balanceFormatted || '0').toFixed(2)} HEP
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Total Sessions */}
          <div className="bg-[#2a2a2a] rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrendingUpIcon className="h-8 w-8 text-purple-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-400">Total Sessions</p>
                <p className="text-2xl font-semibold text-white">
                  {stats.totalSessions}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Time Logging Form */}
        <form onSubmit={handleHeperumSubmit} className="mb-6 max-w-md mx-auto">
          <div className="space-y-4">
            {isAdmin && (
              <div>
                <label className="block text-left text-white mb-2">Date (admin):</label>
                <input
                  type="date"
                  value={sessionDate}
                  onChange={(e) => setSessionDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="heperum-input"
                  disabled={isSubmitting}
                />
              </div>
            )}
            <div>
              <label className="block text-left text-white mb-2">Start time:</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="heperum-input"
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-left text-white mb-2">End time:</label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="heperum-input"
                disabled={isSubmitting}
              />
            </div>

            <button
              type="submit"
              className="heperum-btn-gold w-full py-3 text-lg font-medium"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Logging...' : 'Log time'}
            </button>
          </div>
        </form>

        {/* Payout Request Section */}
        {eligibleSessions.length > 0 && (
          <div className="mb-6 max-w-md mx-auto bg-[#2a2a2a] rounded-lg p-4">
            {!user?.walletAddress && (
              <div className="mb-4 flex items-start gap-2 rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-3">
                <Wallet2 className="h-5 w-5 flex-shrink-0 text-yellow-400" />
                <div className="text-sm text-yellow-200">
                  Add your wallet address before requesting a payout.{' '}
                  <button
                    onClick={() => navigate('/profile')}
                    className="font-semibold text-yellow-100 underline hover:text-white"
                  >
                    Go to Profile
                  </button>
                </div>
              </div>
            )}
            <div className="flex flex-col gap-3 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-white">
                  Selected: {selectedHours.toFixed(1)} hours
                </span>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleSelectAll}
                  className="hidden sm:flex px-4 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-teal-400 transition-colors items-center justify-center gap-2"
                >
                  <CheckSquare className="h-5 w-5" />
                  {selectedSessionIds.size === eligibleSessions.length
                    ? 'Deselect All'
                    : `Select Max Hours (${eligibleSessions.length})`}
                </button>
                <button
                  onClick={handleRequestPayout}
                  disabled={!canRequestPayout || isRequestingPayout || !user?.walletAddress}
                  className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors focus:outline-none focus:ring-2 ${
                    canRequestPayout && user?.walletAddress
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white focus:ring-emerald-400'
                      : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    {isRequestingPayout ? 'Requesting...' : 'Request Payout'}
                  </div>
                </button>
              </div>
            </div>
            {!canRequestPayout && selectedHours > 0 && (
              <p className="text-yellow-400 text-sm">
                Need {(8 - selectedHours).toFixed(1)} more hours to request payout
              </p>
            )}
          </div>
        )}

        {/* Data Table */}
        {sessions.length > 0 && (
          <>
            {/* Mobile Card View - hidden on sm+ screens */}
            <div className="block sm:hidden space-y-3">
              {/* Mobile Select All Checkbox */}
              {eligibleSessions.length > 0 && (
                <div className="flex items-center justify-between bg-[#2a2a2a] rounded-lg p-4 mb-4">
                  <label className="flex items-center gap-3 text-white">
                    <input
                      type="checkbox"
                      checked={selectedSessionIds.size === eligibleSessions.length && eligibleSessions.length > 0}
                      onChange={handleSelectAll}
                      className="w-5 h-5 rounded"
                    />
                    <span className="font-medium">Select All ({eligibleSessions.length})</span>
                  </label>
                  <span className="text-sm text-gray-400">
                    {selectedHours.toFixed(1)} hrs
                  </span>
                </div>
              )}
              {paginatedSessions.map((session) => {
                const status = getSessionStatus(session);
                const selectable = isSessionSelectable(session);
                return (
                  <div key={session.id} className="heperum-mobile-card">
                    {/* Header: Checkbox + Date + Status */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {selectable && (
                          <input
                            type="checkbox"
                            checked={selectedSessionIds.has(session.id)}
                            onChange={() => handleSelectSession(session.id)}
                            className="w-5 h-5"
                          />
                        )}
                        <span className="text-white font-medium">
                          {format(parseISO(session.date), 'MMM d, yyyy')}
                        </span>
                      </div>
                      <span className={getStatusColor(status)}>
                        {getStatusText(session)}
                      </span>
                    </div>

                    {/* Time Range */}
                    <div className="text-white text-lg mb-2">
                      {session.startTime} - {session.endTime}
                    </div>

                    {/* Duration + Actions */}
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">
                        {formatDuration(session.durationMinutes)}
                      </span>
                      {status === 'logged' && (
                        <button
                          onClick={() => handleDelete(session.id, session.date)}
                          className="text-red-400 hover:text-red-300 p-2 touch-target"
                          title="Delete work session"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop Table View - hidden on mobile */}
            <div className="hidden sm:block overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              <table className="heperum-table">
              <thead>
                <tr>
                  <th>
                    {eligibleSessions.length > 0 && (
                      <input
                        type="checkbox"
                        checked={selectedSessionIds.size === eligibleSessions.length && eligibleSessions.length > 0}
                        onChange={handleSelectAll}
                        className="w-4 h-4"
                      />
                    )}
                  </th>
                  <th>Date</th>
                  <th>User</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedSessions.map((session) => {
                  const status = getSessionStatus(session);
                  const selectable = isSessionSelectable(session);
                  return (
                    <tr key={session.id}>
                      <td>
                        {selectable && (
                          <input
                            type="checkbox"
                            checked={selectedSessionIds.has(session.id)}
                            onChange={() => handleSelectSession(session.id)}
                            className="w-4 h-4"
                          />
                        )}
                      </td>
                      <td>{format(parseISO(session.date), 'yyyy-MM-dd')}</td>
                      <td>{user?.email || 'user@example.com'}</td>
                      <td>{session.startTime}</td>
                      <td>{session.endTime}</td>
                      <td>
                        <span className={getStatusColor(status)}>
                          {getStatusText(session)}
                        </span>
                      </td>
                      <td>
                        {status === 'logged' && (
                          <button
                            onClick={() => handleDelete(session.id, session.date)}
                            className="text-red-400 hover:text-red-300 transition-colors"
                            title="Delete work session"
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="heperum-pagination">
              <button
                className="heperum-page-btn"
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
              >
                «
              </button>
              <span className="text-white">Page {currentPage} of {totalPages || 1}</span>
              <button
                className="heperum-page-btn"
                onClick={handleNextPage}
                disabled={currentPage >= totalPages}
              >
                »
              </button>
            </div>
          </>
        )}

        {sessions.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-400 text-lg">No work sessions logged yet.</p>
          </div>
        )}
      </div>
    );
};

export default WorkSessionsPage;
